import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProductItemModel } from "libs/models/batch-model";
import {
  getProductItemByBarcodeService,
  giftCardActivateService,
  giftCardRedeemService,
  giftCardValdiateService,
} from "services/crosspay-service";
import { AsyncAppThunk } from "store/application-state";

export interface ProductOperationsStore {
  cardDetails: ProductItemModel | undefined;
  loading: boolean;
  actionLoading: null | "validate" | "activate" | "redeem" | "recharge" | null;
  error: string | null;
  cardInputValue: string | undefined;
  redemptionAmount: string | undefined;
  rechargeAmount: string | undefined;
}

const initialState: ProductOperationsStore = {
  cardDetails: undefined,
  loading: false,
  actionLoading: null,
  error: null,
  cardInputValue: "",
  redemptionAmount: undefined,
  rechargeAmount: undefined,
};

const slice = createSlice({
  name: "productoperations",
  initialState,
  reducers: {
    setCardDetails: (
      state: ProductOperationsStore,
      action: PayloadAction<ProductItemModel | undefined>
    ) => {
      state.cardDetails = action.payload;
    },
    setLoading: (
      state: ProductOperationsStore,
      action: PayloadAction<boolean>
    ) => {
      state.loading = action.payload;
    },
    setProductOperationsProps: (
      state: ProductOperationsStore,
      action: PayloadAction<{ key: string; value?: any }>
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        [key]: value,
      };
    },
    setErrors: (
      state: ProductOperationsStore,
      action: PayloadAction<string>
    ) => {
      state.error = action.payload;
    },
    setActionLoading: (
      state: ProductOperationsStore,
      action: PayloadAction<
        "validate" | "activate" | "redeem" | "recharge" | null
      >
    ) => {
      state.actionLoading = action.payload;
    },
    setInitialState: (state: ProductOperationsStore) => {
      return initialState;
    },
  },
});

export const {
  setCardDetails,
  setLoading,
  setProductOperationsProps,
  setErrors,
  setActionLoading,
  setInitialState,
} = slice.actions;
export const reducer = slice.reducer;

export const getCardDetailsByBarcode =
  (): AsyncAppThunk => async (dispatch, store) => {
    if (!store().productoperations.cardInputValue) return;
    dispatch(setLoading(true));
    const apiResponse = await getProductItemByBarcodeService(
      store().productoperations.cardInputValue!
    );
    if (
      apiResponse &&
      Object.keys(apiResponse).length > 0 &&
      apiResponse.result !== null
    ) {
      dispatch(setErrors(""));
      dispatch(setCardDetails(apiResponse.result));
    } else {
      dispatch(setCardDetails(undefined));
      dispatch(setErrors(apiResponse?.errorMessage || ""));
    }
    dispatch(setLoading(false));
  };
export const validateCard = (): AsyncAppThunk => async (dispatch, store) => {
  dispatch(setLoading(true));
  dispatch(setActionLoading("validate"));
  const apiResponse = await giftCardValdiateService(
    store().productoperations.cardInputValue!
  );
  if (
    apiResponse &&
    Object.keys(apiResponse).length > 0 &&
    apiResponse.result !== null
  ) {
    dispatch(setErrors(""));
    dispatch(getCardDetailsByBarcode());
  } else {
    dispatch(setErrors(apiResponse?.errorMessage || ""));
  }
  dispatch(setLoading(false));
  dispatch(setActionLoading(null));
};

export const activateteCard = (): AsyncAppThunk => async (dispatch, store) => {
  dispatch(setLoading(true));
  dispatch(setActionLoading("activate"));
  const apiResponse = await giftCardActivateService(
    store().productoperations.cardInputValue!
  );
  if (
    apiResponse &&
    Object.keys(apiResponse).length > 0 &&
    apiResponse.result !== null
  ) {
    dispatch(setErrors(""));
    dispatch(getCardDetailsByBarcode());
  } else {
    dispatch(setErrors(apiResponse?.errorMessage || ""));
  }
  dispatch(setLoading(false));
  dispatch(setActionLoading(null));
};

export const redeemCard = (): AsyncAppThunk => async (dispatch, store) => {
  const cardStore = store().productoperations;
  dispatch(setLoading(true));
  dispatch(setActionLoading("redeem"));
  const apiResponse = await giftCardRedeemService(
    cardStore.cardInputValue!,
    Number(cardStore.redemptionAmount!)
  );
  if (
    apiResponse &&
    Object.keys(apiResponse).length > 0 &&
    apiResponse.result !== null
  ) {
    dispatch(setErrors(""));
    dispatch(
      setProductOperationsProps({ key: "redemptionAmount", value: "" })
    );
    dispatch(getCardDetailsByBarcode());
  }
  dispatch(setLoading(false));
  dispatch(setActionLoading(null));
};
